/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { spendRulesRulesPreview } from "../funcs/spendRulesRulesPreview.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PreviewSpendRuleResult } from "../models/components/previewspendruleresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  PreviewSpendRuleRequest,
  PreviewSpendRuleSecurity,
} from "../models/operations/previewspendrule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SpendRulesPreviewRuleMutationVariables = {
  request: PreviewSpendRuleRequest;
  security?: PreviewSpendRuleSecurity | undefined;
  options?: RequestOptions;
};

export type SpendRulesPreviewRuleMutationData = PreviewSpendRuleResult;

export type SpendRulesPreviewRuleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * previewSpendRule spendRules
 *
 * @remarks
 * Preview which actors a target expression matches and their current spend against a proposed budget. Powers the live preview in the rule editor and the per-actor breakdown in the rule detail view.
 */
export function useSpendRulesPreviewRuleMutation(
  options?: MutationHookOptions<
    SpendRulesPreviewRuleMutationData,
    SpendRulesPreviewRuleMutationError,
    SpendRulesPreviewRuleMutationVariables
  >,
): UseMutationResult<
  SpendRulesPreviewRuleMutationData,
  SpendRulesPreviewRuleMutationError,
  SpendRulesPreviewRuleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSpendRulesPreviewRuleMutation(client, options),
    ...options,
  });
}

export function mutationKeySpendRulesPreviewRule(): MutationKey {
  return ["@gram/client", "rules", "preview"];
}

export function buildSpendRulesPreviewRuleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SpendRulesPreviewRuleMutationVariables,
  ) => Promise<SpendRulesPreviewRuleMutationData>;
} {
  return {
    mutationKey: mutationKeySpendRulesPreviewRule(),
    mutationFn: function spendRulesPreviewRuleMutationFn({
      request,
      security,
      options,
    }): Promise<SpendRulesPreviewRuleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(spendRulesRulesPreview(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
